import { useMemo, useState } from 'react'
import { useParams, useNavigate, useSearch, Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Card, CardContent } from './ui/card'
import { Badge } from './ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { useInterventions, type InterventionKind, type InterventionStatus } from '../hooks/useInterventions'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { useAssetsByUids, useAssetTypes, useContract } from '../hooks/useQueries'
import { AssetPicker } from './AssetPicker'
import { ContractPicker } from './ContractPicker'
import { useLocale } from '../contexts/LocaleContext'
import { ClickablePieChart } from './ui/clickable-pie-chart'
import { PageHeader } from './shared/PageHeader'
import { SECTION_ICONS } from '../lib/section-icons'
import { EmptyState } from './ui/empty-state'
import { AssetTypeTile } from './assets/AssetTypeTile'
import { AssetName } from './ArchivedMarker'

// Exported so the intervention detail page (InterventionPage.tsx) can reuse
// the same badge coloring instead of re-deriving it.
export const KIND_VARIANT: Record<InterventionKind, 'info' | 'warning' | 'secondary'> = {
  preventive: 'info',
  corrective: 'warning',
  works: 'secondary',
}
export const STATUS_VARIANT: Record<InterventionStatus, 'outline' | 'info' | 'warning' | 'success' | 'secondary'> = {
  // A generated occurrence nobody has ordered yet reads as neutral: it is not a
  // demand on anyone, and colouring it would claim otherwise.
  planned: 'secondary',
  requested: 'outline',
  scheduled: 'info',
  assigned: 'info',
  in_progress: 'warning',
  done: 'success',
  cancelled: 'secondary',
}
export const BILLING_VARIANT: Record<string, 'secondary' | 'warning' | 'success' | 'outline'> = {
  included: 'secondary',
  to_bill: 'warning',
  billed: 'success',
  not_applicable: 'outline',
}
export const KINDS: InterventionKind[] = ['preventive', 'corrective', 'works']
export const STATUSES: InterventionStatus[] = ['requested', 'scheduled', 'assigned', 'in_progress', 'done', 'cancelled']

// Cost is stored in integer cents.
export function formatCost(amount?: number | null, currency?: string | null): string {
  if (amount == null) return '—'
  const v = amount / 100
  // No euro fallback: an amount without its unit is incomplete, an amount
  // under the wrong symbol is false. Only the second one misleads.
  return currency
    ? new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(v)
    : v.toLocaleString(undefined, { maximumFractionDigits: 2 })
}

export function InterventionsPage() {
  const { org } = useParams({ from: '/$org/interventions/' })
  const { kind, status, contract_uid: contractUid, asset_uid: assetUid } = useSearch({ from: '/$org/interventions/' })
  const navigate = useNavigate({ from: '/$org/interventions/' })
  const { t } = useTranslation('interventions')
  const { locale } = useLocale()

  const filters = useMemo(() => ({ kind: kind || '', status: status || '', contract_uid: contractUid || '' }), [kind, status, contractUid])
  const { data: interventions = [], isLoading, isError } = useInterventions(org, assetUid, filters)

  // The asset column draws the asset's own type tile (spec 2026-09-17-06). The
  // intervention payload carries only the asset's uid and name, so the type is
  // resolved from the two catalogues the rest of the app already uses.
  const assetUids = useMemo(
    () => Array.from(new Set(interventions.map(iv => iv.asset_uid).filter(Boolean))) as string[],
    [interventions],
  )
  const { data: rowAssets = [] } = useAssetsByUids(org, assetUids)
  const { data: allAssetTypes = [] } = useAssetTypes(org)
  const assetTypeFor = (assetUid: string | null | undefined) => {
    if (!assetUid) return undefined
    const asset = rowAssets.find(a => a.uid === assetUid)
    return asset?.asset_type_uid
      ? allAssetTypes.find(at => at.uid === asset.asset_type_uid)
      : undefined
  }

  const breadcrumbs = useMemo(() => [
    { title: t('common:nav.home', 'Home'), path: `/${org}` },
    { title: t('list.title') },
  ], [org, t])
  useBreadcrumbs(breadcrumbs)

  const formatDate = (s?: string | null) => (s ? new Date(s).toLocaleDateString(locale) : t('list.no_value'))

  // Client-side breakdown for the two "basic visualization" charts — the org-wide
  // fetch above already caps at 200 rows (useInterventions), which is enough to
  // eyeball a distribution without a dedicated aggregation endpoint.
  const kindChartData = useMemo(() => {
    const counts = new Map<string, number>()
    for (const iv of interventions) counts.set(iv.kind, (counts.get(iv.kind) || 0) + 1)
    return Array.from(counts.entries()).map(([k, value]) => ({ name: t(`kind.${k}`, { defaultValue: k }), value, uid: k }))
  }, [interventions, t])

  const statusChartData = useMemo(() => {
    const counts = new Map<string, number>()
    for (const iv of interventions) counts.set(iv.status, (counts.get(iv.status) || 0) + 1)
    return Array.from(counts.entries()).map(([s, value]) => ({ name: t(`status.${s}`, { defaultValue: s }), value, uid: s }))
  }, [interventions, t])

  // The names shown on the filter badges: what the picker just returned, or a
  // lookup by uid after a reload / a link that only carries the uid.
  const [pickedAssetName, setPickedAssetName] = useState<{ uid: string; name: string } | null>(null)
  const [pickedContractName, setPickedContractName] = useState<{ uid: string; name: string } | null>(null)
  const { data: filterAssets = [] } = useAssetsByUids(org, assetUid ? [assetUid] : [])
  const { data: filterContract } = useContract(org, contractUid || '')
  const assetLabel = (pickedAssetName?.uid === assetUid ? pickedAssetName?.name : undefined) || filterAssets.find(a => a.uid === assetUid)?.name || ''
  const contractLabel = (pickedContractName?.uid === contractUid ? pickedContractName?.name : undefined) || filterContract?.name || filterContract?.code || ''

  // Every filter change rebuilds the whole search object: keep all four keys
  // here so changing one never drops another. The list is not paginated (one
  // 200-row fetch), so there is no page to reset.
  const setSearch = (next: Partial<{ kind: string | undefined; status: string | undefined; contract_uid: string | undefined; asset_uid: string | undefined }>) =>
    navigate({ search: { kind, status, contract_uid: contractUid, asset_uid: assetUid, ...next } })
  const setKindFilter = (v: string) => setSearch({ kind: v === 'all' ? undefined : v })
  const setStatusFilter = (v: string) => setSearch({ status: v === 'all' ? undefined : v })

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        icon={SECTION_ICONS.interventions}
        title={t('list.title')}
        description={t('list.subtitle')}
      />

      {/* Visualization */}
      {!isLoading && interventions.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ClickablePieChart
            data={kindChartData}
            title={t('list.chart_by_kind')}
            baseUrl={`/${org}/interventions`}
            queryParam="kind"
          />
          <ClickablePieChart
            data={statusChartData}
            title={t('list.chart_by_status')}
            baseUrl={`/${org}/interventions`}
            queryParam="status"
          />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        {assetUid && (
          <Badge variant="secondary" className="gap-2" data-testid="interventions-asset-filter">
            {t('list.filtered_on_asset', { name: assetLabel })}
            <button
              type="button"
              aria-label={t('list.clear_asset_filter')}
              data-testid="interventions-asset-filter-clear"
              onClick={() => setSearch({ asset_uid: undefined })}
            >
              ×
            </button>
          </Badge>
        )}
        {contractUid && (
          <Badge variant="secondary" className="gap-2" data-testid="interventions-contract-filter">
            {contractLabel ? t('list.filtered_on_contract_named', { name: contractLabel }) : t('list.filtered_on_contract')}
            <button
              type="button"
              aria-label={t('list.clear_contract_filter')}
              data-testid="interventions-contract-filter-clear"
              onClick={() => setSearch({ contract_uid: undefined })}
            >
              ×
            </button>
          </Badge>
        )}
        <Select value={kind || 'all'} onValueChange={setKindFilter}>
          <SelectTrigger className="w-[170px]" data-testid="interventions-kind-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('list.filter_all_kinds')}</SelectItem>
            {KINDS.map((k) => (
              <SelectItem key={k} value={k}>{t(`kind.${k}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={status || 'all'} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[170px]" data-testid="interventions-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('list.filter_all_statuses')}</SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s}>{t(`status.${s}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!assetUid && (
          <div className="w-[240px]">
            <AssetPicker
              org={org}
              testId="interventions-asset-picker"
              placeholder={t('list.asset_picker_placeholder')}
              onChange={(uid, name) => {
                setPickedAssetName({ uid, name })
                setSearch({ asset_uid: uid })
              }}
            />
          </div>
        )}
        {!contractUid && (
          <ContractPicker
            org={org}
            testId="interventions-contract-picker"
            onChange={(uid, name) => {
              setPickedContractName({ uid, name })
              setSearch({ contract_uid: uid })
            }}
          />
        )}
      </div>

      {/* Interventions list */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-64" data-testid="interventions-loading">
              <div className="motion-safe:animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center justify-center h-64" data-testid="interventions-error">
              <p className="text-destructive mb-2">{t('list.error')}</p>
            </div>
          ) : interventions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">{t('list.column_reference')}</th>
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">{t('list.column_title')}</th>
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">{t('list.column_kind')}</th>
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">{t('list.column_status')}</th>
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">{t('list.column_billing')}</th>
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">{t('list.column_scheduled')}</th>
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">{t('list.column_performed')}</th>
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">{t('list.column_asset')}</th>
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">{t('list.column_provider')}</th>
                    <th className="px-4 py-3 text-right text-[11px] font-medium text-muted-foreground uppercase tracking-wider">{t('list.column_cost')}</th>
                  </tr>
                </thead>
                <tbody className="bg-card divide-y divide-border">
                  {interventions.map((iv) => (
                    <tr key={iv.uid} data-testid={`row-intervention-${iv.uid}`}>
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground whitespace-nowrap">{iv.reference}</td>
                      <td className="px-4 py-3 text-sm font-medium text-foreground">
                        <Link
                          to="/$org/interventions/$interventionUid"
                          params={{ org, interventionUid: iv.uid }}
                          className="hover:underline"
                          data-testid={`intervention-detail-link-${iv.uid}`}
                        >
                          {iv.title}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={KIND_VARIANT[iv.kind]}>{t(`kind.${iv.kind}`, { defaultValue: iv.kind })}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={STATUS_VARIANT[iv.status]}>{t(`status.${iv.status}`, { defaultValue: iv.status })}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={BILLING_VARIANT[iv.billing_status] || 'outline'}>
                          {t(`billing.${iv.billing_status}`, { defaultValue: iv.billing_status })}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">{formatDate(iv.scheduled_at)}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">{formatDate(iv.performed_at)}</td>
                      <td className="px-4 py-3">
                        <Link
                          to="/$org/assets/$assetUid"
                          params={{ org, assetUid: iv.asset_uid }}
                          data-testid={`intervention-asset-link-${iv.uid}`}
                        >
                          <Badge variant="outline" className="gap-1.5 text-xs font-normal max-w-[16rem] truncate hover:bg-muted">
                            <AssetTypeTile type={assetTypeFor(iv.asset_uid)} size="sm" />
                            <AssetName name={iv.asset?.name || t('list.asset_unknown')} archivedAt={(iv.asset as { archived_at?: string | null } | null | undefined)?.archived_at} />
                          </Badge>
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{iv.provider?.name || t('list.no_value')}</td>
                      <td className="px-4 py-3 text-sm text-right text-foreground whitespace-nowrap">{formatCost(iv.cost_amount, iv.cost_currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState
              live
              className="py-16"
              icon={SECTION_ICONS.interventions}
              title={t('list.empty')}
              data-testid="interventions-empty"
            />
          )}
        </CardContent>
      </Card>
    </div>
  )
}
